// Onboarding Context - Manages user onboarding flow and profile creation
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useWallet } from './ServiceContext';
import { UserProfileService } from '../services/implementations/UserProfileService';
import { getConfig } from '../config/index';
import { 
  validateWalletProvider, 
  validateWalletAddress, 
  sanitizeProfileData, 
  checkRateLimit, 
  secureStorage,
  sanitizeErrorMessage 
} from '../utils/security';

/**
 * Onboarding Context for managing user profile creation and login flow
 */
const OnboardingContext = createContext(null);

export const OnboardingProvider = ({ children }) => {
  const walletContext = useWallet();
  const { isConnected, address, publicKey } = walletContext;
  
  // Get the actual wallet address with security validation
  const getWalletAddress = useCallback(() => {
    // Secure wallet detection with validation
    
    // First priority: ServiceContext address (most reliable)
    if (address && validateWalletAddress(address)) {
      return address;
    }
    
    // Second priority: publicKey from ServiceContext
    if (publicKey) {
      try {
        const pkAddress = publicKey.toString();
        if (validateWalletAddress(pkAddress)) {
          return pkAddress;
        }
      } catch (error) {
        // Invalid publicKey format
      }
    }
    
    // Secure wallet provider validation
    const walletProviders = [
      { name: 'Phantom', provider: window.solana },
      { name: 'Nightly', provider: window.nightly?.solana },
      { name: 'Solflare', provider: window.solflare }
    ];
    
    for (const { name, provider } of walletProviders) {
      if (provider?.publicKey) {
        try {
          const validation = validateWalletProvider(provider);
          if (!validation.isValid) continue;
          
          const walletAddress = provider.publicKey.toString();
          if (validateWalletAddress(walletAddress)) {
            return walletAddress;
          }
        } catch (error) {
          // Wallet access failed
        }
      }
    }
    
    // Fallback to walletContext with validation
    if (walletContext?.wallet?.publicKey) {
      try {
        const validation = validateWalletProvider(walletContext.wallet);
        if (validation.isValid) {
          const contextAddress = walletContext.wallet.publicKey.toString();
          if (validateWalletAddress(contextAddress)) {
            return contextAddress;
          }
        }
      } catch (error) {
        // WalletContext access failed
      }
    }
    
    return null;
  }, [address, publicKey, walletContext.wallet]);
  
  // Stabilize currentAddress: only update when the actual string value changes
  const rawAddress = getWalletAddress();
  const prevAddressRef = useRef(rawAddress);
  const currentAddress = useMemo(() => {
    if (rawAddress !== prevAddressRef.current) {
      prevAddressRef.current = rawAddress;
    }
    return prevAddressRef.current;
  }, [rawAddress]);
  
  // Debug logging for wallet context
  // OnboardingProvider wallet context and address determined
  
  // Monitor ServiceContext changes (cleaned up - working correctly now)
  useEffect(() => {
    if (config.ENABLE_DEBUG_LOGS && isConnected && address) {
      // ServiceContext wallet connected
    }
  }, [isConnected, address]);

  // Restore wallet connection and onboarding state on mount
  useEffect(() => {
    const restoreConnectionAndState = async () => {
      try {
        console.log('🔄 Restoring wallet connection and onboarding state...');
        
        // Try to restore wallet connection
        const storedWalletRaw = secureStorage.getItem(WALLET_STORAGE_KEY);
        const storedWalletInfo = storedWalletRaw ? (typeof storedWalletRaw === 'string' ? storedWalletRaw : JSON.stringify(storedWalletRaw)) : null;
        const storedOnboardingRaw = secureStorage.getItem(ONBOARDING_STORAGE_KEY);
        const storedOnboardingState = storedOnboardingRaw ? (typeof storedOnboardingRaw === 'string' ? storedOnboardingRaw : JSON.stringify(storedOnboardingRaw)) : null;
        
        if (storedWalletInfo) {
          const walletInfo = JSON.parse(storedWalletInfo);
          console.log('💾 Found stored wallet info:', walletInfo);
          
          // Skip auto-reconnect if user explicitly disconnected
          const userDisconnected = sessionStorage.getItem('znoospace_user_disconnected');
          if (userDisconnected) {
            console.log('🔌 User disconnected — skipping auto-reconnect');
            sessionStorage.removeItem('znoospace_user_disconnected');
          } else if (window.solana && window.solana.isPhantom) {
            try {
              await window.solana.connect({ onlyIfTrusted: true });
              console.log('✅ Auto-reconnected to Phantom wallet');
            } catch (error) {
              console.log('⚠️ Could not auto-reconnect to Phantom:', error.message);
            }
          }
        }
        
        // Restore onboarding state if available
        if (storedOnboardingState) {
          const savedState = JSON.parse(storedOnboardingState);
          console.log('💾 Found stored onboarding state:', savedState);
          
          // Handle both flat format {status, profile, timestamp, ...}
          // and legacy nested format {state: {status, profile}, timestamp}
          const stateData = savedState.state || savedState;
          const stateProfile = stateData.profile || null;
          const stateStatus = stateData.status || (stateProfile ? 'completed' : undefined);
          const stateTimestamp = savedState.timestamp || stateData.timestamp;
          
          // CRITICAL: Check if the stored profile matches the current wallet address
          const currentWalletAddress = currentAddress || (window.solana?.publicKey?.toString());
          const storedWalletAddress = stateProfile?.walletAddress || stateProfile?.author;
          
          // Only restore if wallet addresses match AND state is recent (within 24 hours)
          const stateAge = Date.now() - new Date(stateTimestamp).getTime();
          const walletMatches = currentWalletAddress && storedWalletAddress && currentWalletAddress === storedWalletAddress;
          
          if (walletMatches && stateAge < 24 * 60 * 60 * 1000) {
            // If we have a profile (regardless of status), restore it directly without fresh check
            if (stateProfile && (stateStatus === 'completed' || stateStatus === 'welcome')) {
              setOnboardingState(prev => ({
                ...prev,
                status: 'completed',
                profile: stateProfile,
                txId: stateData.txId || null,
                showWelcome: false,
                isRestoring: false
              }));
              console.log('✅ Restored completed profile state (wallet matches):', stateProfile.displayName || stateProfile.username);
              return; // CRITICAL: Exit early to prevent fresh blockchain check
            } else {
              // For states without profile, allow fresh check
              setOnboardingState(prev => ({
                ...prev,
                status: 'idle',
                isRestoring: false
              }));
              console.log('✅ Restored onboarding state for fresh check (no profile found)');
            }
          } else {
            // Clear old data if wallet doesn't match or state is too old
            if (!walletMatches && currentWalletAddress) {
              console.log('🗑️ Clearing old profile data - wallet address mismatch');
              
              secureStorage.removeItem(ONBOARDING_STORAGE_KEY);
              secureStorage.removeItem(WALLET_STORAGE_KEY);
              
              if (storedWalletAddress) {
                secureStorage.removeItem(`profile_${storedWalletAddress}`);
                secureStorage.removeItem(`znoospace_profile_${storedWalletAddress}`);
              }
              
              setOnboardingState({
                status: 'idle',
                profile: null,
                txId: null,
                error: null,
                showProfileModal: false,
                showWelcome: false,
                isRestoring: false
              });
              
              console.log('✅ Cleared old data, ready for new wallet onboarding');
            } else {
              console.log('⚠️ Stored onboarding state is too old, ignoring');
            }
          }
        }
        
        // Set loading complete after a short delay
        setTimeout(() => {
          setOnboardingState(prev => ({
            ...prev,
            status: prev.status === 'loading' ? 'idle' : prev.status,
            isRestoring: false
          }));
        }, 1500); // Show loading for at least 1.5 seconds
        
      } catch (error) {
        console.error('❌ Error restoring connection and state:', error);
        setOnboardingState(prev => ({
          ...prev,
          status: 'idle',
          isRestoring: false,
          error: 'Failed to restore connection'
        }));
      }
    };
    
    restoreConnectionAndState();
  }, []);
  
  const [onboardingState, setOnboardingState] = useState({
    status: 'loading', // 'loading', 'idle', 'checking', 'new_user', 'creating_profile', 'completed', 'welcome'
    profile: null,
    txId: null,
    error: null,
    showProfileModal: false,
    showWelcome: false,
    welcomeDisplayName: null,
    isRestoring: true
  });

  const config = getConfig();
  const profileService = useRef(new UserProfileService()).current;
  const checkOnboardingStatusRef = useRef();
  const WALLET_STORAGE_KEY = 'znoospace_wallet_connection';
  const ONBOARDING_STORAGE_KEY = 'znoospace_onboarding_state';

  /**
   * Check the onboarding status for a wallet address
   */
  const checkOnboardingStatus = useCallback(async (walletAddress) => {
    setOnboardingState(prev => ({
      ...prev,
      status: 'checking',
      error: null
    }));

    try {
      if (config.ENABLE_DEBUG_LOGS) {
        // Checking onboarding status
      }

      const onboardingStatus = await profileService.getOnboardingStatus(walletAddress);
      
      if (config.ENABLE_DEBUG_LOGS) {
        console.log('📋 Onboarding status:', onboardingStatus);
      }

      switch (onboardingStatus.status) {
        case 'new':
          // First-time user - show profile creation modal
          setOnboardingState(prev => ({
            ...prev,
            status: 'new_user',
            showProfileModal: true
          }));
          break;

        case 'pending':
          // Profile creation in progress - check transaction status
          setOnboardingState(prev => ({
            ...prev,
            status: 'pending',
            txId: onboardingStatus.txId,
            profile: onboardingStatus.profile
          }));
          break;

        case 'completed':
          // Returning user - show welcome message first, then complete
          let displayName = onboardingStatus.profile?.displayName || onboardingStatus.profile?.username;
          // Sanitize — displayName may be an object or '[object Object]' from corrupted on-chain data
          if (displayName && typeof displayName === 'object') {
            displayName = displayName.displayName || displayName.name || displayName.username || null;
          }
          if (!displayName || displayName === '[object Object]') {
            displayName = onboardingStatus.profile?.username || `User ${currentAddress?.slice(0, 8) || ''}`;
          }
          
          // CRITICAL: Save the profile data immediately when found
          const profileToSave = {
            state: {
              status: 'completed',
              profile: onboardingStatus.profile,
              txId: onboardingStatus.txId,
              showWelcome: false,
              welcomeDisplayName: displayName
            },
            timestamp: new Date().toISOString()
          };
          secureStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(profileToSave));
          console.log('💾 SAVED profile data for future refreshes:', displayName);
          
          setOnboardingState(prev => ({
            ...prev,
            status: 'welcome',
            profile: onboardingStatus.profile,
            txId: onboardingStatus.txId,
            showWelcome: true,
            welcomeDisplayName: displayName
          }));
          
          if (config.ENABLE_DEBUG_LOGS) {
            console.log(`✅ Welcome back, ${displayName}!`);
          }
          
          // Auto-transition to completed after showing welcome
          setTimeout(() => {
            setOnboardingState(prev => ({
              ...prev,
              status: 'completed',
              showWelcome: false
            }));
          }, 3000); // Show welcome for 3 seconds
          break;

        default:
          throw new Error(`Unknown onboarding status: ${onboardingStatus.status}`);
      }

    } catch (error) {
      console.error('❌ Error checking onboarding status:', error);
      setOnboardingState(prev => ({
        ...prev,
        status: 'error',
        error: error.message
      }));
    }
  }, [config.ENABLE_DEBUG_LOGS]);

  // Manual onboarding check - only works with properly connected wallet
  // Update the ref when checkOnboardingStatus changes
  useEffect(() => {
    checkOnboardingStatusRef.current = checkOnboardingStatus;
  }, [checkOnboardingStatus]);

  // Guard against duplicate onboarding checks (shared between manual and auto-check)
  const lastCheckedAddressRef = useRef(null);

  const manualOnboardingCheck = useCallback(async () => {
    console.log('🔄 Manual onboarding check triggered');
    
    const walletAddr = getWalletAddress();
    if (!walletAddr) {
      console.log('❌ No wallet connected - cannot check onboarding status');
      setOnboardingState({
        status: 'idle',
        profile: null,
        txId: null,
        error: 'Wallet must be connected to check profile status',
        showProfileModal: false
      });
      return;
    }
    
    // Mark as checked so the auto-check effect doesn't duplicate this
    lastCheckedAddressRef.current = walletAddr;
    
    console.log('✅ Checking onboarding status for connected wallet:', walletAddr);
    if (checkOnboardingStatusRef.current) {
      await checkOnboardingStatusRef.current(walletAddr);
    }
  }, [getWalletAddress]);

  // Automatically check onboarding status when wallet connects via ServiceContext
  // Note: App.js also calls manualOnboardingCheck after WalletConnectionScreen connect,
  // so we guard against duplicate checks with a ref.
  useEffect(() => {
    if (isConnected && currentAddress) {
      // Only auto-check if we haven't already checked this address
      if (lastCheckedAddressRef.current !== currentAddress) {
        lastCheckedAddressRef.current = currentAddress;
        if (checkOnboardingStatusRef.current) {
          checkOnboardingStatusRef.current(currentAddress);
        }
      }
    } else if (!isConnected) {
      lastCheckedAddressRef.current = null;
      // Reset onboarding state when wallet disconnects
      setOnboardingState({
        status: 'idle',
        profile: null,
        txId: null,
        error: null,
        showProfileModal: false
      });
    }
  }, [isConnected, currentAddress]);

  /**
   * Create a new user profile with security validation
   */
  const createProfile = async (profileData) => {
    if (!currentAddress) {
      throw new Error('Wallet not connected');
    }

    console.log('🚀 OnboardingProvider: Creating profile for wallet');

    // Rate limiting check
    const rateLimitResult = checkRateLimit('PROFILE_UPDATE', currentAddress);
    if (!rateLimitResult.allowed) {
      const error = `Rate limit exceeded. Please wait before creating another profile. Remaining requests: ${rateLimitResult.remaining}`;
      setOnboardingState(prev => ({
        ...prev,
        error: error,
        showProfileModal: false
      }));
      throw new Error(error);
    }

    // Sanitize and validate profile data
    let sanitizedProfileData;
    try {
      sanitizedProfileData = sanitizeProfileData(profileData);
      console.log('✅ Profile data sanitized successfully');
    } catch (error) {
      const sanitizedError = sanitizeErrorMessage(error);
      setOnboardingState(prev => ({
        ...prev,
        error: sanitizedError,
        showProfileModal: false
      }));
      throw new Error(sanitizedError);
    }

    setOnboardingState(prev => ({
      ...prev,
      status: 'creating_profile',
      error: null,
      showProfileModal: false  // Close modal immediately when creation starts
    }));

    try {
      // Create the profile using the service with sanitized data
      // Extract individual parameters from sanitizedProfileData object
      const { username, bio = '', avatarUrl = '' } = sanitizedProfileData;
      
      // Parameter extraction completed
      
      const result = await profileService.createProfile(currentAddress, username, bio, avatarUrl);
      
      console.log('🚀 Profile creation completed');
      
      if (result.success) {
        console.log('✅ Profile created successfully!');
        
        // Update state with the new profile
        setOnboardingState(prev => ({
          ...prev,
          status: 'welcome',
          profile: result.profile,
          txId: result.txId,
          showWelcome: true,
          welcomeDisplayName: result.profile.displayName,
          showProfileModal: false
        }));
        
        // Save profile data securely for future refreshes
        const profileDataToSave = {
          status: 'completed',
          profile: result.profile,
          txId: result.txId,
          displayName: result.profile.displayName,
          timestamp: Date.now()
        };
        
        // Use secure storage for sensitive profile data
        secureStorage.setItem('onboarding_state', profileDataToSave);
        // Keep legacy storage for compatibility during transition
        secureStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(profileDataToSave));
        console.log('💾 SAVED profile data securely for future refreshes');
        
        // Auto-dismiss welcome after 3 seconds
        setTimeout(() => {
          setOnboardingState(prev => ({
            ...prev,
            status: 'completed',
            showWelcome: false
          }));
        }, 3000);

        return { success: true, profile: result.profile, txId: result.txId };
      } else {
        throw new Error(result.error || 'Failed to create profile');
      }

    } catch (error) {
      const sanitizedError = sanitizeErrorMessage(error);
      console.error('❌ Error creating profile:', sanitizedError);
      setOnboardingState(prev => ({
        ...prev,
        status: 'new_user',
        error: sanitizedError,
        showProfileModal: false  // Close modal on error too
      }));
      throw new Error(sanitizedError);
    }
  };

  /**
   * Close the profile creation modal
   */
  const closeProfileModal = () => {
    setOnboardingState(prev => ({
      ...prev,
      showProfileModal: false
    }));
  };

  /**
   * Dismiss welcome message and go to posting
   */
  const dismissWelcome = () => {
    setOnboardingState(prev => ({
      ...prev,
      status: 'completed',
      showWelcome: false
    }));
  };

  /**
   * Retry onboarding process
   */
  const retryOnboarding = () => {
    if (currentAddress) {
      checkOnboardingStatus(currentAddress);
    }
  };

  /**
   * Get user profile
   */
  const getProfile = async (walletAddress = currentAddress) => {
    if (!walletAddress) {
      return { success: false, error: 'Wallet address required' };
    }

    try {
      return await profileService.getProfile(walletAddress);
    } catch (error) {
      console.error('Error getting profile:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Update user profile
   */
  const updateProfile = async (updates) => {
    if (!currentAddress) {
      throw new Error('Wallet not connected');
    }

    try {
      const result = await profileService.updateProfile(currentAddress, updates);
      
      if (result.success) {
        setOnboardingState(prev => ({
          ...prev,
          profile: result.profile
        }));
      }

      return result;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  /**
   * Check if username is available
   */
  const checkUsernameAvailability = async (username, options = {}) => {
    try {
      return await profileService.checkUsernameAvailability(username, options);
    } catch (error) {
      console.error('Error checking username availability:', error);
      return { available: false, error: error.message };
    }
  };

  /**
   * Clear all profile data (for testing)
   */
  const clearAllData = () => {
    profileService.clearAllData();
    setOnboardingState({
      status: 'idle',
      profile: null,
      txId: null,
      error: null,
      showProfileModal: false
    });
  };

  const contextValue = {
    // State
    onboardingState,
    isNewUser: onboardingState.status === 'new_user',
    isCreatingProfile: onboardingState.status === 'creating_profile',
    isCompleted: onboardingState.status === 'completed',
    isWelcoming: onboardingState.status === 'welcome',
    isLoading: onboardingState.status === 'loading' || onboardingState.isRestoring,
    hasProfile: !!onboardingState.profile,
    profile: onboardingState.profile,
    txId: onboardingState.txId,
    error: onboardingState.error,
    showProfileModal: onboardingState.showProfileModal,
    showWelcome: onboardingState.showWelcome,
    welcomeDisplayName: onboardingState.welcomeDisplayName,

    // Wallet information
    wallet: walletContext.wallet,
    address: currentAddress,
    isConnected: isConnected && !!currentAddress,
    publicKey: publicKey,

    // Methods
    createProfile,
    closeProfileModal,
    dismissWelcome,
    retryOnboarding,
    getProfile,
    updateProfile,
    checkUsernameAvailability,
    clearAllData,
    manualOnboardingCheck,

    // Utility
    formatAddress: (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '',
  };

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};

/**
 * Hook to access onboarding context
 */
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  
  return context;
};

// Export the context for direct access if needed
export { OnboardingContext };
